/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  ErrorMessageValidator,
  PasswordRequirementTooltip,
  SignUpFormValidator,
} from "@advapacs/common";
import { useAppDispatch } from "@src/config/store";
import { TFunction } from "i18next";
import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import { SignUpAction } from "../ducks/slices";

interface SignUpCredentialsFormProps {
  t: TFunction;
  wrapperClass?: string;
  disabled?: boolean;
  errorMessageText?: string;
  email?: string;
  isSocialSignUp?: boolean;
}

interface ICredentialState {
  email: string;
  password: string;
  confirmPassword: string;
}

const defaultCredential: Readonly<ICredentialState> = {
  email: "",
  password: "",
  confirmPassword: "",
};

export const SignUpCredentialsForm = forwardRef(
  (props: SignUpCredentialsFormProps, ref) => {
    const dispatch = useAppDispatch();
    const {
      t,
      wrapperClass = "",
      disabled = false,
      errorMessageText,
      email,
      isSocialSignUp = false,
    } = props;

    const [model, setModel] = useState<ICredentialState>(defaultCredential);
    const validator = useMemo(() => {
      return SignUpFormValidator(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, model]);
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      if (errorMessageText && name === "email" && value !== "") {
        dispatch(SignUpAction.clearErrorMessage()); // clear unique error message.
      }

      setModel({ ...model, [name]: value });
    };

    useImperativeHandle(
      ref,
      () => ({
        validate() {
          return {
            accountValidator: validator,
          };
        },
        getData() {
          return { accountModel: model };
        },
      }),
      [model, validator]
    );

    useEffect(() => {
      if (email && isSocialSignUp) {
        setModel({
          ...defaultCredential,
          email: email,
        });
      }
    }, [email, isSocialSignUp]);

    return (
      <div className={`account-information ${wrapperClass}`}>
        <div className="form-group">
          <label htmlFor="email" className="col-form-label ">
            {t("common:activate_account.email")}
            <span className="text-danger">*</span>
          </label>
          <div className="">
            <input
              type="text"
              className="form-control"
              placeholder={t("common:activate_account.email")}
              name="email"
              value={model.email}
              disabled={disabled || isSocialSignUp}
              onChange={handleInputChange}
            />
            {errorMessageText && (
              <span className={`form-text text-danger `}>
                {errorMessageText}
              </span>
            )}

            <ErrorMessageValidator
              errorMessage={validator.message(
                t("common:activate_account.email"),
                model.email,
                "required|email|disposableEmail"
              )}
            />
          </div>
        </div>
        {!isSocialSignUp && (
          <>
            <div className="form-group">
              <label htmlFor="password" className="col-form-label ">
                {t("login:password")}
                <span className="text-danger">*</span>
              </label>
              <div className="">
                <PasswordRequirementTooltip>
                  <input
                    type="password"
                    disabled={disabled}
                    className="form-control"
                    value={model.password}
                    onChange={handleInputChange}
                    placeholder={t("login:password")}
                    name="password"
                    autoComplete="new-password"
                  />
                </PasswordRequirementTooltip>
                <ErrorMessageValidator
                  errorMessage={validator.message(
                    `${t("login:password")}`,
                    model.password,
                    "required|passwordPattern"
                  )}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword" className="col-form-label ">
                {t("login:confirm_password")}
                <span className="text-danger">*</span>
              </label>
              <div className="">
                <input
                  type="password"
                  disabled={disabled}
                  className="form-control"
                  value={model.confirmPassword}
                  onChange={handleInputChange}
                  placeholder={t("login:confirm_password")}
                  name="confirmPassword"
                  autoComplete="new-password"
                />
                <ErrorMessageValidator
                  errorMessage={
                    model.confirmPassword
                      ? validator.message(
                          `${t("login:confirm_password")}`,
                          {
                            password: model.password,
                            confirmPassword: model.confirmPassword,
                          },
                          "comparePassword"
                        )
                      : validator.message(
                          `${t("login:confirm_password")}`,
                          model.confirmPassword,
                          "required"
                        )
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);
